import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { ReactNode } from "react";

import { formatCnpj } from "utils/federal-tax-id";

import { Cabecalho, Cartao, ErroAba, SemCadastro, dataSemHora, diaBrt, num, plural, seloDaAba } from "./comum";
import { ProximoMes } from "./ProximoMes";
import { QuemVeCadaAba, SeloVisibilidade } from "./Visibilidade";
import { Baldes, Faixa, PrevistoResposta, VisibilidadeResposta } from "./types";

type Rotulo = "data informada pela folha" | "estimativa" | "limite do seu plano não configurado";

// Ninguém entra em 0-18 por aniversário (ruling da v1.0): a chave fica fora da soma.
const somaFaixas = (f: Partial<Record<Faixa, number>> | undefined): number =>
  Object.entries(f ?? {}).reduce((t, [k, v]) => (k === "0-18" ? t : t + v), 0);

const colunas = (b: Baldes): [number, number, number] => [b["0-30"], b["31-90"], b["91-180"] + b["181-365"]];

function Certeza({ rotulo }: { rotulo: Rotulo }) {
  return <Chip size="small" variant="outlined" label={rotulo} sx={{ mr: 0.5 }} />;
}

type Linha = { chave: string; rotulo: string; contrato?: string; certeza: Rotulo[]; nota?: string; extra?: ReactNode; fazer: string; valores: (number | null)[]; semCadastro?: string };

function LinhaTabela({ l }: { l: Linha }) {
  return (
    <TableRow data-testid={`linha-${l.chave}`}>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>{l.rotulo}</Typography>
        {l.contrato && <Typography variant="body2" color="text.secondary">{l.contrato}</Typography>}
        <Box my={0.5}>{l.certeza.map((c) => <Certeza key={c} rotulo={c} />)}</Box>
        {l.nota && <Typography variant="body2" color="text.secondary">{l.nota}</Typography>}
        {l.extra}
        {l.fazer && <Typography variant="body2" color="text.secondary">{l.fazer}</Typography>}
      </TableCell>
      {l.semCadastro !== undefined ? (
        <TableCell colSpan={3}>
          <SemCadastro motivo={l.semCadastro} testid={`previsto-${l.chave}`} />
        </TableCell>
      ) : [30, 90, 365].map((d, i) => (
        <TableCell key={d} align="right" data-testid={`celula-${l.chave}-${d}`}>
          {l.valores[i] === null ? <Typography variant="body2" color="text.secondary">ainda sem aviso</Typography> : num(l.valores[i] as number)}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function PrevistoTab({ dados, erro, visibilidade }: { dados?: PrevistoResposta; erro: boolean; visibilidade: VisibilidadeResposta }) {
  if (erro || !dados) return <ErroAba nome="O que vem aí" />;
  const aoLadoDoTitulo = (
    <>
      <SeloVisibilidade selo={seloDaAba(visibilidade, "previsto")} />
      <QuemVeCadaAba abas={visibilidade.abas} />
    </>
  );
  const cab = { sobretitulo: "O que vem aí", titulo: "O que a folha já informou sobre os próximos meses", aoLadoDoTitulo };
  if (!dados.disponivel) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Cabecalho {...cab} dados={{ diaDosNumeros: null, avisos: [] }} />
        <Typography color="text.secondary">Ainda não apurado para esta empresa.</Typography>
      </Box>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Cabecalho
        {...cab}
        dados={{ diaDosNumeros: dados.dataCiclo, avisos: [] }}
        unidade={`${plural(dados.previsto.vinculosAtivos, "vínculo ativo", "vínculos ativos")} no CNPJ ${formatCnpj(dados.cnpj)}; dependentes declarados no cadastro`}
      />
      <Cartao titulo="O que vem, por período">
        <Box sx={{ overflowX: "auto" }}>
        <Table size="small" data-testid="tabela-previsto">
          <TableHead>
            <TableRow>
              {["Acontecimento", "Próximos 30 dias", "31 a 90 dias", "91 a 365 dias"].map((h, i) => (
                <TableCell key={h} align={i ? "right" : "left"}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{linhas(dados).map((l) => <LinhaTabela key={l.chave} l={l} />)}</TableBody>
        </Table>
        </Box>
        {dados.previsto.contratosComPrazo.vencidosSemInformacao > 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="previsto-vencidos" mt={1}>
            {dados.previsto.contratosComPrazo.vencidosSemInformacao === 1
              ? "1 contrato passou da data de término sem nova informação da folha"
              : `${num(dados.previsto.contratosComPrazo.vencidosSemInformacao)} contratos passaram da data de término sem nova informação da folha`}
          </Typography>
        )}
        {dados.previsto.cadastro.disponivel && !dados.dependentesPorContrato.some((c) => c.porIdade.length > 0) && <NotasFilhos p={dados.previsto} />}
      </Cartao>
      <ProximoMes dataCiclo={dados.dataCiclo} carga={dados.previsto.cargaDoProximoMes} motivosQueANormaProtege={dados.motivosQueANormaProtege} />
      <Typography variant="caption" color="text.secondary">
        {`eSocial lido até ${diaBrt(dados.fonteAte)}`}
        {dados.previsto.cadastro.disponivel && (
          <span data-testid="rodape-cadastro">{` · Cadastro de ${dataSemHora(dados.previsto.cadastro.lidoEm)}`}</span>
        )}
      </Typography>
    </Box>
  );
}

function NotasFilhos({ p }: { p: Disponivel["previsto"] }) {
  const nTit = p.titularesSemCadastro ?? 0;
  const nDep = p.dependentesSemTipo ?? 0;
  return (
    <>
      {nTit > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="previsto-titulares-sem-cadastro">
          {nTit === 1 ? "1 titular sem cadastro nesta empresa" : `${num(nTit)} titulares sem cadastro nesta empresa`}
        </Typography>
      )}
      {nDep > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="previsto-dependentes-sem-tipo">
          {nDep === 1 ? "1 dependente sem parentesco reconhecido" : `${num(nDep)} dependentes sem parentesco reconhecido`}
        </Typography>
      )}
    </>
  );
}

type Disponivel = Extract<PrevistoResposta, { disponivel: true }>;

function linhas({ previsto: p, dependentesPorContrato }: Disponivel): Linha[] {
  const sem = p.cadastro.disponivel ? undefined : p.cadastro.motivo;
  const folha: Rotulo = "data informada pela folha";
  const licenca = (chave: string, rotulo: string, r: Baldes & { estimados: number }, fazer: string, extra?: ReactNode): Linha => ({
    chave, rotulo, fazer, extra, valores: colunas(r),
    certeza: r.estimados > 0 ? [folha, "estimativa"] : [folha],
    nota: r.estimados > 0 ? `${plural(r.estimados, "retorno", "retornos")} com fim pelo prazo legal` : undefined,
  });
  const semFimN = p.retornosDeLicenca.outras.semFimInformado;
  const semFim = semFimN > 0 && (
    <Typography variant="body2" color="text.secondary" data-testid="previsto-sem-fim">
      {semFimN === 1 ? "1 afastado" : `${num(semFimN)} afastados`} sem data de retorno informada pela folha
    </Typography>
  );
  const notasFilhos = <NotasFilhos p={p} />;
  let primeira = 0;
  const filhos: Linha[] = sem !== undefined
    ? [{ chave: "sem-cadastro-filhos", rotulo: "Filhos que fazem a idade-limite", certeza: [], fazer: "", valores: [], semCadastro: sem }]
    : dependentesPorContrato.flatMap((c) =>
    c.porIdade.map((i): Linha => ({
      chave: `filhos-${c.workspaceId}-${i.idade}`,
      rotulo: `Filhos que fazem ${i.idade} anos`,
      contrato: dependentesPorContrato.length > 1 ? c.contrato : undefined,
      certeza: c.idade === null ? [folha, "limite do seu plano não configurado"] : [folha],
      extra: primeira++ === 0 ? notasFilhos : undefined,
      fazer: "Avise a família com antecedência: na idade-limite o filho sai do plano.",
      valores: colunas(i.baldes),
    })),
  );
  return [
    { chave: "bebes", rotulo: "Bebês esperados", certeza: [folha], valores: [p.nascimentosEsperados["0-30"], null, null],
      fazer: "Inclua o bebê no plano em até 30 dias do nascimento para entrar sem carência." },
    licenca("maternidade", "Retornos de licença-maternidade", p.retornosDeLicenca.maternidade, "Confira se o bebê já está no plano antes da volta."),
    licenca("outras", "Retornos de outras licenças", p.retornosDeLicenca.outras, "Confira se a pessoa volta com o plano ativo e os dados em dia.", semFim),
    ...filhos,
    sem !== undefined
      ? { chave: "sem-cadastro-faixa", rotulo: "Mudanças de faixa etária", certeza: [], fazer: "", valores: [], semCadastro: sem }
      : { chave: "faixas", rotulo: "Mudanças de faixa etária", certeza: [folha], valores: [
        somaFaixas(p.mudancasDeFaixa?.["0-30"]), somaFaixas(p.mudancasDeFaixa?.["31-90"]),
        somaFaixas(p.mudancasDeFaixa?.["91-180"]) + somaFaixas(p.mudancasDeFaixa?.["181-365"])],
      fazer: "Quem muda de faixa pode passar a custar mais: confira o reajuste com a operadora antes." },
    { chave: "experiencia", rotulo: "Contratos de até 90 dias (em geral, de experiência)", certeza: [folha],
      valores: colunas(p.contratosComPrazo.experiencia),
      fazer: "Na data, a pessoa é efetivada ou sai: decida antes para o plano acompanhar." },
    { chave: "outros-prazos", rotulo: "Outros contratos com prazo que vencem", certeza: [folha],
      valores: colunas(p.contratosComPrazo.outrosPrazos),
      fazer: "Confirme a renovação ou a saída antes da data." },
  ];
}
